import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CodaltComponent } from 'app/codalt.component';
import { projectAccess, projectOverviewAccess } from '../auth.guard';
import { Customer } from '../customer';
import { Filter } from '../filter';
import { AuthService } from '../services/auth/auth.service';
import { AuthorisationService } from '../services/auth/authorisation.service';
import { CustomerService } from '../services/customer/customer.service';
import { PageTitleService } from '../services/page-title/page-title.service';
import { SearchService } from '../services/search/search.service';

const replaceRegex = /^(gemeente|provincie)\s+/i;

@Component({
    selector: 'app-customers-overview',
    templateUrl: './customers-overview.component.html',
    styleUrls: ['./customers-overview.component.scss'],
    standalone: false,
})
export class CustomersOverviewComponent extends CodaltComponent implements OnInit, OnDestroy {
    public customers: Customer[] = [];

    public searchedCustomers: Customer[] = [];

    public search: string;

    public sortAlphabetically = false;

    @ViewChild('tooltip') tooltip: MatTooltip;
    @ViewChild('sortSearchTemplate', { static: true }) sortSearchTemplate;

    constructor(
        private customerService: CustomerService,
        private router: Router,
        private authService: AuthService,
        private searchService: SearchService,
        private pageTitleService: PageTitleService
    ) {
        super();
    }

    ngOnInit() {
        this.sortAlphabetically = this.LocalStorage.getCustomersSortAlphabetically();

        setTimeout(() => (this.searchService.searchTemplate = this.sortSearchTemplate));

        this.pageTitleService.setTitle('Klantgebieden');
        if (AuthorisationService.hasFeature(this.Features.CustomersRecentSorted)) {
            this.subscriptions.add(
                this.customerService.getRecentSortedList().subscribe(customers => {
                    this.customers = customers;
                    this.searchedCustomers = this.customers;

                    if (this.sortAlphabetically) {
                        this.searching();
                    }

                    if (this.customers.length === 1) {
                        const customer = this.customers[0];
                        this.LocalStorage.setCustomer(customer);
                        this.router.navigate([this.Routenames.customers, customer.id], { replaceUrl: true });
                    }
                })
            );
            this.search = this.searchService.value;
            this.subscriptions.add(
                this.searchService.change.subscribe(searchValue => {
                    this.search = searchValue;
                    this.searching();
                })
            );
        } else {
            this.router.navigate([this.Routenames.timeline], { replaceUrl: true });
        }
    }

    ngOnDestroy(): void {
        this.searchService.searchTemplate = null;

        super.ngOnDestroy();
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter' && this.searchedCustomers.length === 1) {
            this.openCustomer(this.searchedCustomers[0]);
            event.preventDefault();
        }
    }

    toggleCustomerSorting(): void {
        this.sortAlphabetically = !this.sortAlphabetically;

        this.LocalStorage.setCustomersSortAlphabetically(this.sortAlphabetically);

        this.searching();
    }

    openCustomer(customer: Customer) {
        this.LocalStorage.setFilter(new Filter());
        if (customer.consumer_customer) {
            this.router.navigate(['']);
        } else {
            // Without project access the projects page returns a 403, so never navigate there.
            const overviewAccess = AuthorisationService.hasAnyFunctionality(projectOverviewAccess);
            if (customer['hasCustomerProjects'] > 0 && overviewAccess) {
                this.router.navigate([this.Routenames.customers, customer.id, this.Routenames.projects]);
            } else {
                this.router.navigate([this.Routenames.customers, customer.id]);
            }
        }
    }

    searching() {
        if (!this.search) {
            this.searchedCustomers = this.customers;
        } else {
            this.searchedCustomers = [];

            this.customers.forEach(customer => {
                if (customer.name.toLowerCase().indexOf(this.search.toLowerCase()) !== -1) {
                    this.searchedCustomers.push(customer);
                }
            });
        }

        if (this.sortAlphabetically) {
            this.searchedCustomers = [...this.searchedCustomers].sort((left, right) =>
                left.name
                    .replace(replaceRegex, '')
                    .localeCompare(right.name.replace(replaceRegex, ''), 'nl', { sensitivity: 'base' })
            );
        }

        if (this.searchedCustomers.length === 1) {
            this.tooltip.show();
        } else {
            this.tooltip.hide();
        }
    }

    logout() {
        this.authService.logout().subscribe(() => this.router.navigate([this.Routenames.logout], { replaceUrl: true }));
    }
}
